import { ActionIcon, Avatar, Burger, Menu, UnstyledButton } from "@mantine/core";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Check,
  ChevronDown,
  CircleHelp,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Users,
  Zap,
} from "lucide-react";

import { useGetTeamBalance } from "../../api/generated/endpoints/billing/billing";
import { useGetMe } from "../../api/generated/endpoints/identity/identity";
import { useListBrands } from "../../api/generated/endpoints/teams/teams";
import type { Brand, TeamRole } from "../../api/generated/model";
import { managesBrands } from "../../platform/permissions";
import { UserMenu, useSchemeSwitch } from "../../platform/shell/UserMenu";
import { formatCount } from "../../ui/format";
import { Logo } from "../../ui/Logo";
import { Soon } from "../../ui/Soon";
import { HOME2, SETTINGS, sectionPath } from "./nav";
import { UiSwitch } from "../../platform/ui-switch/UiSwitch";
import { useStudioSection } from "./StudioNav";
import classes from "./studio2.module.css";

/** A brand's square mark: its logo, else its initials. */
function Swatch({ brand }: { brand: Brand }) {
  return (
    <Avatar
      size={18}
      radius={5}
      src={brand.logo_url}
      alt=""
      name={brand.name}
      color="initials"
      className={classes.swatch}
    />
  );
}

/** The prototype's brand button and its menu: the team's brands, then adding and managing them. */
function BrandMenu({ teamId, brandId, role }: { teamId: string; brandId: string; role: TeamRole }) {
  const brands = useListBrands(teamId).data;
  const current = brands?.find((brand) => brand.id.toLowerCase() === brandId.toLowerCase());
  const section = useStudioSection();
  const navigate = useNavigate();
  if (current === undefined) return null;
  return (
    <Menu position="bottom-start" width={240}>
      <Menu.Target>
        <UnstyledButton className={classes.brandButton} aria-label={`Brand: ${current.name}`}>
          <Swatch brand={current} />
          <span className={`${classes.brandName} ${classes.narrowHidden}`}>{current.name}</span>
          <ChevronDown size={14} className={classes.caret} aria-hidden />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Brand</Menu.Label>
        {brands?.map((brand) => (
          <Menu.Item
            key={brand.id}
            classNames={{ itemLabel: classes.brandItem }}
            leftSection={<Swatch brand={brand} />}
            rightSection={brand.id === current.id ? <Check size={14} aria-hidden /> : null}
            onClick={() => {
              void navigate({ to: sectionPath(section), params: { teamId, brandId: brand.id } });
            }}
          >
            {brand.name}
          </Menu.Item>
        ))}
        <Menu.Divider />
        {/* The brands area's own pages, inside Pro (spec ui-switch-classic-simple-pro, e4). */}
        {managesBrands(role) ? (
          <Menu.Item
            onClick={() =>
              void navigate({
                to: "/teams/$teamId/brands/$brandId/home2/brands/new",
                params: { teamId, brandId: current.id },
              })
            }
          >
            + Add brand
          </Menu.Item>
        ) : null}
        <Menu.Item
          onClick={() =>
            void navigate({
              to: "/teams/$teamId/brands/$brandId/home2/brands",
              params: { teamId, brandId: current.id },
            })
          }
        >
          Manage brands
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

// Only this file marks the current mode: TanStack would also mark Pro Studio's link on Simple, a
// path under it.
const EXACT = { exact: true };

/**
 * The team's credits, the balance the current usage pill reads (one request, one cache). A readout:
 * its plans and top-ups need billing (#254; spec, decision 4).
 */
function Credits({ teamId }: { teamId: string }) {
  const balance = useGetTeamBalance(teamId).data;
  if (balance === undefined) return null;
  return (
    <span
      className={`${classes.credits} ${classes.narrowHidden}`}
      title="Credits left this billing period"
    >
      <Zap size={15} className={classes.bolt} aria-hidden />
      <span className={classes.number}>{formatCount(balance.available)}</span>
      <span className={`${classes.dim} ${classes.phoneHidden} ${classes.mediumHidden}`}>credits</span>
    </span>
  );
}

function ThemeButton() {
  const { Icon, label, flip } = useSchemeSwitch();
  return (
    <ActionIcon
      variant="subtle"
      size="lg"
      className={classes.iconButton}
      aria-label={label}
      title={label}
      onClick={flip}
    >
      <Icon size={18} aria-hidden />
    </ActionIcon>
  );
}

/** A header control whose page or menu is not built yet. */
function SoonIcon({ label, icon: Icon }: { label: string; icon: typeof Settings }) {
  return (
    <Soon>
      {(props) => (
        <ActionIcon
          variant="subtle"
          size="lg"
          className={classes.iconButton}
          aria-label={label}
          {...props}
        >
          <Icon size={18} aria-hidden />
        </ActionIcon>
      )}
    </Soon>
  );
}

/**
 * Classic's "Admin Panel", for staff only: the staff pages, in this header's frame, Pro's or
 * Simple's, which their `?ui=` names (spec ui-perf/admin-panel-pro-simple). Current on them. The
 * API checks again on every call.
 */
function StaffLink({ simple }: { simple: boolean }) {
  const isStaff = useGetMe({ query: { select: (me) => me.is_staff } }).data === true;
  if (!isStaff) return null;
  return (
    <ActionIcon
      renderRoot={(props) => (
        <Link to="/staff" search={{ ui: simple ? "simple" : "pro" }} {...props} />
      )}
      variant="subtle"
      size="lg"
      className={classes.iconButton}
      aria-label="Admin Panel"
    >
      <Users size={18} aria-hidden />
    </ActionIcon>
  );
}

interface HeaderProps {
  teamId: string;
  brandId: string;
  role: TeamRole;
  /** Simple's header, which has no nav. */
  simple: boolean;
  /** Below 48em, the nav's drawer and its button; absent on Simple, which has no nav. */
  drawer?: { opened: boolean; toggle: () => void } | undefined;
  /** From 48em, the nav open or compact and the current shell's button that switches it. */
  nav?: { open: boolean; toggle: () => void } | undefined;
}

/**
 * The prototype's one top bar: the logo, the brand, Classic, Simple or Pro on the left; the
 * credits, help, light or dark, settings and the account on the right.
 */
export function StudioHeader({ teamId, brandId, role, simple, drawer, nav }: HeaderProps) {
  const Toggle = nav?.open === true ? PanelLeftClose : PanelLeftOpen;
  return (
    <header className={classes.header}>
      {drawer === undefined ? null : (
        <Burger size="sm" opened={drawer.opened} onClick={drawer.toggle} aria-label="Navigation" />
      )}
      {/* The current shell's sidebar button, its icons and words (spec ui-switch, e8). */}
      {nav === undefined ? null : (
        <ActionIcon
          variant="subtle"
          size="lg"
          className={classes.iconButton}
          aria-label={nav.open ? "Collapse the sidebar" : "Expand the sidebar"}
          aria-expanded={nav.open}
          onClick={nav.toggle}
        >
          <Toggle size={20} strokeWidth={1.5} aria-hidden />
        </ActionIcon>
      )}
      <Link
        to={HOME2}
        params={{ teamId, brandId }}
        activeOptions={EXACT}
        className={`${classes.logo} ${classes.phoneHidden} ${classes.logoHidden}`}
        aria-label="Pro Studio home"
      >
        <Logo width={110} height={16} />
      </Link>
      <BrandMenu teamId={teamId} brandId={brandId} role={role} />
      {/* Classic, Simple or Pro (spec ui-perf/ui-switch-classic-simple-pro). */}
      <UiSwitch teamId={teamId} />
      <span className={classes.grow} />
      <Credits teamId={teamId} />
      <span className={`${classes.tools} ${classes.phoneHidden}`}>
        <SoonIcon label="Help" icon={CircleHelp} />
        <ThemeButton />
        <StaffLink simple={simple} />
        <ActionIcon
          renderRoot={(props) => <Link to={SETTINGS} params={{ teamId, brandId }} {...props} />}
          variant="subtle"
          size="lg"
          className={classes.iconButton}
          aria-label="Settings"
        >
          <Settings size={18} aria-hidden />
        </ActionIcon>
      </span>
      {/* Its header shows the pill at every width: the menu needs none. */}
      <span className={classes.account}>
        <UserMenu teamId={teamId} role={role} pro={{ brandId }} />
      </span>
    </header>
  );
}
